import { upgradeProperty } from '../../utils/upgradeProperty/upgradeProperty.js';
import { cxModuleLoader } from './cx-loader.js';
import { render } from './react-render.js';
import { fetchCampaignData } from './http.js';
import AnalyticsService from './analytics';
import ContextService from '../context';
import DisclaimerService from '../disclaimer';
import template from './template.js';
import { createResizeListener } from '../../utils/events/resize.js';
import { wrapperEvent } from '../../widgets/oc-connect-widget/eventEmitter.js';

let devEnv = '';
try {
  devEnv = !import.meta.env.PROD;
} catch {}

export function camelCase(str) {
  return str.replace(/^.|-./g, function (letter, index) {
    return index === 0 ? letter.toLowerCase() : letter.substr(1).toUpperCase();
  });
}

export function validateCampaign(campaign) {
  if (campaign?.widgetVersion && campaign?.journey) return true;
  return false;
}

const defaultAttributes = ['slot-id', 'override-url', 'theme'];

const WidgetElement = ({
  attributes = [],
  Component,
  contract,
  customCss = '',
  cxClient,
  disclaimer = () => {},
  fetchCampaign = attr => fetchCampaignData(attr.slotId, attr.overrideUrl),
  fontLoader = () => {},
  loader = null,
  name,
  style = '',
  themes = () => '',
  version
}) => {
  const wEvent = (eventName, detail) =>
    new CustomEvent(`${name}:${eventName}`, {
      bubbles: true,
      composed: true,
      detail
    });

  return class extends HTMLElement {
    constructor() {
      super();

      if (!this.shadowRoot) {
        this.attachShadow({ mode: 'open' });
        this.shadowRoot.appendChild(
          template(customCss).content.cloneNode(true)
        );
      }

      // stylesEl houses the `themes` data
      this.stylesEl = this.shadowRoot.getElementById('widget-styles');

      // these styles are different from the `themes` data, and are the styles of the 'pure npm' cx-module css
      if (style) {
        const styleTag = document.createElement('style');
        styleTag.textContent = style;
        this.shadowRoot.prepend(styleTag);
      }

      if (version) this._version = version;
    }

    fireResizeEvent = (data, eventOrigin) => {
      switch (eventOrigin) {
        case 'widget': {
          return this.dispatchEvent(wEvent('resize', data));
        }
        case 'wrapper': {
          return this.dispatchEvent(wrapperEvent('resize', data));
        }

        default: {
          return;
        }
      }
    };

    async connectedCallback() {
      [...defaultAttributes, ...attributes].forEach(key => {
        Object.defineProperty(this, camelCase(key), {
          get() {
            return this.getAttribute(key);
          },
          set(value) {
            this.setAttribute(key, value);
          }
        });
        upgradeProperty(this, key);
      });
      this.bootstrap();
    }

    async bootstrap() {
      const loadingSlot = this.shadowRoot.querySelector('slot[name="loading"]');
      const errorSlot = this.shadowRoot.querySelector('slot[name="error"]');
      loadingSlot.hidden = false;
      let resizeObserverCleanupFn;

      try {
        const getRootElement = () => this.shadowRoot.getElementById('root');
        const target = getRootElement();

        // Use pre-loaded component if available, otherwise load from cxClient
        const { Component: LoadedComponent } = Component
          ? { Component }
          : await cxModuleLoader(cxClient, this.shadowRoot);

        const campaignData = validateCampaign(this._initialData)
          ? this._initialData
          : await fetchCampaign(this);

        if (!LoadedComponent || !target) {
          return;
        }

        const analytics = AnalyticsService(campaignData);

        ContextService({
          ...campaignData,
          slotId: this.slotId,
          overrideUrl: this.overrideUrl
        });

        // set (from campaign) or override (from theme attribute) widgetStyle
        campaignData.widgetStyle =
          this.getAttribute('theme') || campaignData.widgetStyle || 'default';
        this.setAttribute('theme', campaignData.widgetStyle);

        fontLoader(campaignData.widgetStyle, this.shadowRoot);

        this.stylesEl.append(themes(campaignData.widgetStyle));

        DisclaimerService(disclaimer(campaignData), this);

        const props = await contract(
          {
            ...campaignData,
            analytics
          },
          this
        );

        render(target, LoadedComponent, props, loader);

        this.dispatchEvent(wEvent('load-success', campaignData));
        resizeObserverCleanupFn = createResizeListener({
          selectorFn: getRootElement,
          fireEventFn: this.fireResizeEvent,
          hasConnectWrapper: this.getAttribute('hasConnectWrapper')
        });
      } catch (error) {
        errorSlot.hidden = false;
        if (devEnv) console.error(error);

        this.dispatchEvent(wEvent('load-error', { error }));
        resizeObserverCleanupFn?.();
      } finally {
        loadingSlot.hidden = true;

        this.dispatchEvent(wEvent('load'));
      }
    }
  };
};

export function widgetBootstrap(props) {
  if (!window.customElements || window.customElements.get(props.name)) return;

  const widget = WidgetElement(props);
  window.customElements.define(props.name, widget);
}
